import { ColumnDef } from "@tanstack/react-table";
import { Spinner } from "components";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import NewCustomersPage from "pages/Customers/newcustomer";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { checkLevel } from "common/rules";
import { LEVEL_2 } from "common/constants";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useContacts } from "./hooks/useContacts"; // Import our new hook
import { LeadContactsProps, PageDetails } from "./apis"; // Import shared types
import { useDebounce } from "use-debounce";

// --- Constants can be defined outside the component ---
const allcolumns: ColumnDef<LeadContactsProps>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
    cell: ({ row }) => <div className="">{row.getValue("id")}</div>,
    enableHiding: false,
    enableSorting: false,
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
  },
  {
    id: "mobile",
    accessorKey: "mobile",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Mobile"
      />
    ),
    cell: ({ row }) => {
      return <p className="text-center w-full">{row.getValue("mobile")}</p>;
    },
  },
  {
    id: "contactStatus",
    accessorKey: "contactStatus",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Status"
      />
    ),
    cell: ({ row }) => {
      const status = contact_status.find(
        (status) => status.value === row.getValue("contactStatus")
      );
      if (!status) return null;
      return (
        <p
          style={{ backgroundColor: status.bgColor, color: status.textColor }}
          className="py-[3px] text-sm leading-4 text-center font-medium max-w-[160px] px-2 mx-auto rounded-md"
        >
          {status.label}
        </p>
      );
    },
  },
];

const tabData = [
  { label: "All", value: "all", enabled: true },
  { label: "Customers", value: "customers", enabled: true },
];

const contact_status = [
  { value: "customer", label: "Customer", textColor: "#23AB28", bgColor: "#E1FFE2" },
  { value: "contact", label: "Contact", textColor: "#9a9a9a", bgColor: "#e5e5e5" },
];

// --- Main Component ---
export default function LeadContacts() {
  const navigate = useNavigate();
  const [activeTab, setTab] = useState("all");
  
  // State for pagination and search
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  
  // Debounced search value to avoid firing API calls on every keystroke
  const debouncedSearch = useDebounce(search, 500); // Using your existing debounce hook

  // Use our custom hook for data fetching.
  // The query is only enabled when the 'all' tab is active.
  const { data, isLoading, isFetching } = useContacts(
    debouncedSearch[0],
    page,
    perPage,
    activeTab === "all"
  );

  // Safely access data, providing default values.
  const contacts = data?.contacts ?? [];
  const pageDetails = data?.pageDetails;

  const onRowClick = (row: any) => {
    // Logic remains identical
    if (activeTab === "customers") return;
    if (row?.contactStatus === "contact") {
      navigate(`/leads/contact/${row?.id}`);
    } else {
      navigate(`/customerdetails/${row?.id}`);
    }
  };

  // When tab changes, reset pagination
  const handleTabChange = (value: string) => {
      setTab(value);
      setPage(1);
      setSearch("");
  }
  
  const showSpinner = isLoading || isFetching;

  return (
    <>
      {showSpinner && <Spinner />}
      <div className="flex h-full gap-10 py-5 px-6 sm:px-0 w-full flex-col overflow-hidden">
        {/* Top section with Tabs and Buttons - Logic is unchanged */}
        <div className="flex gap-5 justify-between w-full items-center">
          <Tabs
            className="bg-navbar-menu-bg w-fit flex rounded-lg gap-2"
            value={activeTab}
            onValueChange={handleTabChange}
          >
            <TabsList className="h-[44px]">
              {tabData.map((tab, index: number) => (
                <TabsTrigger
                  key={index}
                  value={tab.value}
                  disabled={!tab.enabled}
                  className={`py-3 px-10 disabled:opacity-40 disabled:cursor-not-allowed text-sm h-[44px] min-w-[150px] font-semibold ${
                    tab.value === activeTab
                      ? "border border-blue-700 bg-white-A700 text-blue-700 rounded-lg"
                      : " text-black-label"
                  }`}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {activeTab !== "all" && !checkLevel(LEVEL_2) && (
            <Link className="sm:hidden" to={"/customerregistration"}>
              <button
                disabled={checkLevel(LEVEL_2)}
                className="rounded-lg py-[11px] px-4 disabled:opacity-40 border bg-blue-700 text-white-A700 font-semibold text-sm"
              >
                Register customer
              </button>
            </Link>
          )}
        </div>

        <div className="flex gap-5 justify-between w-full items-center">
          {activeTab === "all" && (
            <div className="relative flex items-center w-[500px]">
              <Input
                placeholder={"Search by name or mobile number"}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1); // Reset to first page on new search
                }}
                className="py-3 pl-10 pr-4 flex gap-3.5 rounded-[30px] w-full"
              />
              <Search className="absolute left-2 h-3.5" />
            </div>
          )}
          {activeTab === "all" && (
            <Link className="sm:hidden" to={"/lead/createcontact"}>
              <button className="rounded-lg py-[11px] px-4 border bg-blue-700 text-white-A700 font-semibold text-sm">
                + Add Contact
              </button>
            </Link>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 relative overflow-auto">
          {activeTab === "customers" ? (
            <NewCustomersPage />
          ) : (
            <DataTable
              columns={allcolumns}
              data={contacts}
              onRowClick={(row) => onRowClick(row)} // Pass row.original for full data object
              isToolbarVisible={false}
              isPaginationVisible={true}
              isPageCountVisible={false}
              defaultSorting={[{ id: "id", desc: true }]} // This is now a presentational default
              onClickNextPage={() => setPage((page) => page + 1)}
              onClickPreviousPage={() => setPage((page) => page - 1)}
              onGotoPage={(page) => setPage(page)}
              totalPages={pageDetails?.pageTotal}
              currentPage={pageDetails?.curPage}
              canNextPage={!!pageDetails?.nextPage}
              canPreviousPage={!!pageDetails?.prevPage}
            />
          )}
        </div>

        {/* Floating Action Buttons for mobile - Logic is unchanged */}
        {activeTab === "all" ? (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/lead/createcontact"}
          >
            <button className="rounded-lg py-[11px] px-4 border w-full bg-blue-700 text-white-A700 font-semibold text-sm">
              + Add Contact
            </button>
          </Link>
        ) : (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/customerregistration"}
          >
            <button className="rounded-lg py-[11px] px-4 border w-full bg-blue-700 text-white-A700 font-semibold text-sm">
              Register customer
            </button>
          </Link>
        )}
        <Toaster position="top-right" />
      </div>
    </>
  );
}